/**
 * Business Demo skin (Issue #20) — wizard-форма для бизнес-заказчиков.
 *
 * Audience: бизнес-заказчик / маркетинг / sales / регулятор. **Не** инженер.
 * Цель: «потыкать» Nikira DLT без знания JSON / Gateway endpoints / Ed25519.
 *
 * Architecture:
 *
 *   1. **Wizard state machine** — 6 шагов (Phase F bumped from 5):
 *      deal type → buyer → seller → amount/date → **documents (optional)** →
 *      confirm. Каждый шаг валидируется на стороне UI; «Далее» disabled
 *      пока required-поля пустые. Documents step is optional —
 *      `canAdvance` defaults to true so users without attachments
 *      skip it with one click.
 *
 *   2. **Pre-populated mock counterparties** (`lib/business-mock-data.ts`)
 *      — 5 buyers + 5 sellers с реалистичными отраслями. Counterparties
 *      = display-only LABELS; реальные wire-addresses derive-ятся из
 *      env priv keys via `GET /api/party-addresses` (Issue #14/#15/#16
 *      4-tier validation requires pubkey↔party binding, Issue #21 fix).
 *
 *   3. **Confirm step** — POST к Gateway через generic `callProxy()`
 *      (тот же `dispatchEndpoint` который использует Endpoint Explorer
 *      и Scenario Runner). Auto-signing reuses existing canonical
 *      pipeline. Wizard maps deal-template → endpoint id
 *      (`case_create` для transfers, `contract_create` для composite).
 *
 *   4. **Right-side `SidePanel`** (Phase F, Jun 3 2026) — 3 tabs:
 *      - «Структура» (default) — Russian-language nested view of the
 *        contract (договор → стороны → соглашения). For non-engineers.
 *      - «JSON» — raw `spec_full` preview as it will be POSTed to
 *        Gateway, with disclaimer. For engineers / auditors / regulators.
 *      - «События» — live SSE webhook stream with business-friendly
 *        labels via `translateEventType()`. Auto-switches to this tab
 *        when the first post-submit event lands.
 *
 *   5. **Acts UI** (Phase F B-NEW-4) — after successful composite-contract
 *      registration, the Confirm step shows «Зафиксировать поставку»
 *      panel for incremental `POST /contract/{id}/act` submissions
 *      (amount + description + optional attachments). Only rendered
 *      for delivery-* templates where act-based closure is the contract's
 *      lifecycle. Wire-coverage: `contract-delivery-with-prepayment`
 *      and `contract-delivery-post-payment` scenarios.
 *
 *   6. **File attachments** (Phase F B-NEW-3) — wizard step 4 («Документы»)
 *      uploads files via `POST /api/storage/upload` (base64-JSON wrapper
 *      around Gateway's multipart endpoint). Resulting `file_hash` is
 *      threaded into `spec_full.attachments[]` so contracts can carry
 *      content-addressed document references.
 *
 * Out of scope per Issue #20:
 *   - Multi-tenancy
 *   - Persistence between sessions
 *   - Real authentication beyond mock
 *   - Mobile-responsive design
 *   - i18n (RU only)
 *   - Business-friendly error-injection (FailureModePanel остаётся
 *     engineer-only)
 */
import { useEffect, useMemo, useRef, useState, type ChangeEvent } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { callProxy, fetchPartyAddresses, uploadFile } from '@/lib/api';
import {
  DEAL_TEMPLATES,
  MOCK_BUYERS,
  MOCK_SELLERS,
  findCounterpartyById,
  findDealTemplateById,
  type DealTemplate,
  type MockCounterparty,
} from '@/lib/business-mock-data';
import { computeBuyerPreMint } from '@/lib/business-pre-mint';
import { formatAmount, formatTimestamp, translateEventType } from '@/lib/business-rename';
import { cn } from '@/lib/cn';
import { isProxyOk, type ProxyResult, type WebhookEntry } from '@/lib/types';

// Phase F: 6-step wizard (was 5-step). New step 4 «Документы (опционально)»
// inserts between «Сумма и срок» (step 3) and «Подтверждение» (now step 5).
// The new step is OPTIONAL — `canAdvance` defaults to true so users who
// don't need attachments skip it with one click.
type Step = 0 | 1 | 2 | 3 | 4 | 5;

const STEP_LABELS: readonly string[] = [
  'Тип сделки',
  'Покупатель',
  'Продавец',
  'Сумма и срок',
  'Документы',
  'Подтверждение',
];

const CONFIRM_STEP: Step = 5;

/** Local snapshot of an upload — kept in form state so users can review
 *  attachments before submit AND so `buildGatewayBody` can thread the
 *  file_hash into `spec_full.attachments[]`. */
export interface AttachmentSnapshot {
  readonly fileHash: string;
  readonly mime: string;
  readonly sizeBytes: number;
  readonly filename: string;
  readonly description: string;
}

interface FormState {
  dealId: DealTemplate['id'] | null;
  buyerId: string | null;
  sellerId: string | null;
  amount: string;
  executeAt: string;
  attachments: AttachmentSnapshot[];
}

type SubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'success'; result: ProxyResult; submittedAt: string }
  | { kind: 'error'; message: string };

const INITIAL_FORM: FormState = {
  dealId: null,
  buyerId: null,
  sellerId: null,
  amount: '',
  executeAt: '',
  attachments: [],
};

function isStepComplete(step: Step, form: FormState): boolean {
  switch (step) {
    case 0:
      return form.dealId !== null;
    case 1:
      return form.buyerId !== null;
    case 2:
      return form.sellerId !== null && form.sellerId !== form.buyerId;
    case 3: {
      const amountOk = /^\d+(\.\d{1,2})?$/.test(form.amount) && Number.parseFloat(form.amount) > 0;
      if (!amountOk) return false;
      const template = form.dealId === null ? undefined : findDealTemplateById(form.dealId);
      if (template !== undefined && template.requiresDate) {
        return form.executeAt.length > 0;
      }
      return true;
    }
    case 4:
      // «Документы» — opt-in; user can advance with 0 attachments.
      return true;
    case 5:
      return true;
    default:
      return false;
  }
}

export function BusinessDemo() {
  const [step, setStep] = useState<Step>(0);
  const [form, setForm] = useState<FormState>(INITIAL_FORM);
  const [submit, setSubmit] = useState<SubmitState>({ kind: 'idle' });
  const [partyAddrs, setPartyAddrs] = useState<{ addrA: string; addrB: string } | null>(null);
  const [partyErr, setPartyErr] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchPartyAddresses()
      .then((p) => {
        if (!cancelled) setPartyAddrs(p);
      })
      .catch((e: unknown) => {
        if (!cancelled) setPartyErr((e as Error).message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const dealTemplate = form.dealId === null ? null : (findDealTemplateById(form.dealId) ?? null);
  const buyer = form.buyerId === null ? null : (findCounterpartyById(form.buyerId) ?? null);
  const seller = form.sellerId === null ? null : (findCounterpartyById(form.sellerId) ?? null);
  const canAdvance = isStepComplete(step, form);

  const goNext = () => {
    if (step < CONFIRM_STEP && canAdvance) {
      setStep(((step as number) + 1) as Step);
    }
  };
  const goBack = () => {
    if (step > 0) {
      setStep(((step as number) - 1) as Step);
    }
  };

  const reset = () => {
    setStep(0);
    setForm(INITIAL_FORM);
    setSubmit({ kind: 'idle' });
  };

  const onSubmit = async () => {
    if (
      dealTemplate === null ||
      partyAddrs === null ||
      buyer === null ||
      seller === null ||
      form.amount === ''
    ) {
      return;
    }
    setSubmit({ kind: 'submitting' });
    const submittedAt = new Date().toISOString();
    try {
      const body = buildGatewayBody({
        template: dealTemplate,
        addrA: partyAddrs.addrA,
        addrB: partyAddrs.addrB,
        amount: form.amount,
        executeAt: form.executeAt,
        attachments: form.attachments,
      });

      // Sandbox pre-fund step — see lib/business-pre-mint.ts. The
      // deployed sandbox starts every container with an empty world;
      // any wizard template whose buyer has an outflow leg (immediate
      // or scheduled) would default with `INSUFFICIENT_BALANCE` without
      // this step. Production OP integrations never do this — users are
      // funded via legitimate channels — but the wizard targets demos
      // and must be self-contained.
      const preMintAmount = computeBuyerPreMint(body, partyAddrs.addrA);
      if (preMintAmount !== null) {
        const mintResult = await callProxy('admin_mint', {
          body: {
            address: partyAddrs.addrA,
            amount: preMintAmount,
            reason: `BusinessDemo sandbox pre-fund (${dealTemplate.title})`,
          },
        });
        if (!isProxyOk(mintResult) || mintResult.gateway_status_code >= 300) {
          const message = isProxyOk(mintResult)
            ? `Sandbox pre-fund вернул HTTP ${mintResult.gateway_status_code} — не удалось пополнить баланс покупателя.`
            : `Sandbox pre-fund не дошёл до Gateway: ${mintResult.error_message}`;
          setSubmit({ kind: 'error', message });
          return;
        }
      }

      const result = await callProxy(dealTemplate.gatewayEndpoint, { body });
      setSubmit({ kind: 'success', result, submittedAt });
    } catch (e) {
      setSubmit({ kind: 'error', message: (e as Error).message });
    }
  };

  return (
    <div className="px-6 py-6 max-w-7xl mx-auto space-y-6">
      <PageIntro />
      {partyErr !== null && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="text-sm text-red-900">
            <strong>Не удалось получить адреса участников:</strong> {partyErr}
            <div className="mt-2 text-xs text-red-700">
              Проверьте, что emulator-сервер запущен с корректными
              <code className="font-mono mx-1">EMULATOR_PRIV_A</code>/
              <code className="font-mono mx-1">EMULATOR_PRIV_B</code>.
            </div>
          </CardContent>
        </Card>
      )}
      <Stepper currentStep={step} />
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_400px] gap-6 items-start">
        <div className="space-y-4 min-w-0">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">
                Шаг {step + 1} из {STEP_LABELS.length}: {STEP_LABELS[step]}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {step === 0 && (
                <StepDealType
                  selectedId={form.dealId}
                  onSelect={(dealId) => setForm({ ...form, dealId })}
                />
              )}
              {step === 1 && (
                <StepCounterparty
                  role="buyer"
                  counterparties={MOCK_BUYERS}
                  selectedId={form.buyerId}
                  excludeId={form.sellerId}
                  onSelect={(id) => setForm({ ...form, buyerId: id })}
                />
              )}
              {step === 2 && (
                <StepCounterparty
                  role="seller"
                  counterparties={MOCK_SELLERS}
                  selectedId={form.sellerId}
                  excludeId={form.buyerId}
                  onSelect={(id) => setForm({ ...form, sellerId: id })}
                />
              )}
              {step === 3 && dealTemplate !== null && (
                <StepAmountDate
                  template={dealTemplate}
                  amount={form.amount}
                  executeAt={form.executeAt}
                  onAmountChange={(amount) => setForm({ ...form, amount })}
                  onDateChange={(executeAt) => setForm({ ...form, executeAt })}
                />
              )}
              {step === 4 && (
                <StepAttachments
                  attachments={form.attachments}
                  onAdd={(a) =>
                    setForm((prev) => ({ ...prev, attachments: [...prev.attachments, a] }))
                  }
                  onRemove={(idx) =>
                    setForm((prev) => ({
                      ...prev,
                      attachments: prev.attachments.filter((_, i) => i !== idx),
                    }))
                  }
                />
              )}
              {step === 5 && dealTemplate !== null && buyer !== null && seller !== null && (
                <StepConfirm
                  template={dealTemplate}
                  buyer={buyer}
                  seller={seller}
                  amount={form.amount}
                  executeAt={form.executeAt}
                  attachments={form.attachments}
                  submit={submit}
                  onSubmit={() => void onSubmit()}
                  onReset={reset}
                  contractIdFromSubmit={
                    submit.kind === 'success' ? extractContractId(submit.result) : null
                  }
                  partyAddrs={partyAddrs}
                />
              )}
            </CardContent>
          </Card>
          <div className="flex justify-between">
            <Button
              variant="ghost"
              onClick={goBack}
              disabled={step === 0 || submit.kind !== 'idle'}
            >
              ← Назад
            </Button>
            {step < CONFIRM_STEP ? (
              <Button onClick={goNext} disabled={!canAdvance}>
                Далее →
              </Button>
            ) : (
              <span className="text-xs text-slate-500 self-center">
                Используйте кнопку «Создать договор» выше
              </span>
            )}
          </div>
        </div>
        <SidePanel
          form={form}
          dealTemplate={dealTemplate}
          buyer={buyer}
          seller={seller}
          partyAddrs={partyAddrs}
          submittedAt={submit.kind === 'success' ? submit.submittedAt : null}
        />
      </div>
    </div>
  );
}

function PageIntro() {
  return (
    <div className="rounded-lg border border-slate-200 bg-gradient-to-br from-slate-50 to-white px-5 py-4">
      <h2 className="text-lg font-semibold text-slate-900">Создание договора в DLT</h2>
      <p className="text-sm text-slate-600 mt-1 leading-relaxed">
        Заполните форму в 5 шагов — система зарегистрирует ваш договор в распределённом реестре
        Nikira. Подписание криптографическими ключами происходит автоматически. Лента событий ниже
        покажет статус сделки в режиме реального времени.
      </p>
    </div>
  );
}

interface StepperProps {
  readonly currentStep: Step;
}

function Stepper({ currentStep }: StepperProps) {
  return (
    <ol className="flex items-center gap-2 text-xs">
      {STEP_LABELS.map((label, idx) => {
        const isActive = idx === currentStep;
        const isComplete = idx < currentStep;
        return (
          <li key={label} className="flex items-center gap-2 flex-1">
            <div
              className={cn(
                'flex items-center justify-center w-7 h-7 rounded-full font-medium border-2 transition-colors',
                isActive && 'bg-brand text-white border-brand',
                isComplete && 'bg-emerald-500 text-white border-emerald-500',
                !isActive && !isComplete && 'bg-white text-slate-400 border-slate-300',
              )}
              aria-current={isActive ? 'step' : undefined}
            >
              {isComplete ? '✓' : idx + 1}
            </div>
            <span
              className={cn('truncate', isActive ? 'text-slate-900 font-medium' : 'text-slate-500')}
            >
              {label}
            </span>
            {idx < STEP_LABELS.length - 1 && (
              <span className="flex-1 h-px bg-slate-200 ml-1" aria-hidden="true" />
            )}
          </li>
        );
      })}
    </ol>
  );
}

// ── SidePanel (Phase F B-NEW-2) ─────────────────────────────────

type SideTab = 'structure' | 'json' | 'events';

interface SidePanelProps {
  readonly form: FormState;
  readonly dealTemplate: DealTemplate | null;
  readonly buyer: MockCounterparty | null;
  readonly seller: MockCounterparty | null;
  readonly partyAddrs: { addrA: string; addrB: string } | null;
  readonly submittedAt: string | null;
}

/**
 * Right-side preview panel. Three tabs:
 *
 *   - «Структура» — Russian-language nested view of the contract (договор
 *     → соглашения → стороны → суммы → даты). Designed for non-engineer
 *     audience: no JSON, no curly braces.
 *
 *   - «JSON» — raw `spec_full` preview as it will be POSTed to Gateway.
 *     Disclaimer at the top notes this is the **request body**, not the
 *     final on-chain record (signatures stripped + tx metadata absent).
 *     For engineers / auditors / regulators who want to see the wire
 *     format.
 *
 *   - «События» — live SSE stream of webhooks (subscribed at mount).
 *     Re-uses `BusinessTimelineEntry` for individual rows. Count badge
 *     on the tab shows post-submit event count.
 */
function SidePanel({ form, dealTemplate, buyer, seller, partyAddrs, submittedAt }: SidePanelProps) {
  const [tab, setTab] = useState<SideTab>('structure');
  const [eventEntries, setEventEntries] = useState<WebhookEntry[]>([]);
  const [connState, setConnState] = useState<'connecting' | 'open' | 'lost'>('connecting');

  useEffect(() => {
    setConnState('connecting');
    const es = new EventSource('/api/webhooks');
    const onMessage = (e: MessageEvent<string>) => {
      try {
        const entry = JSON.parse(e.data) as WebhookEntry;
        setEventEntries((prev) => [entry, ...prev].slice(0, 50));
      } catch {
        // ignore malformed SSE frame
      }
    };
    const onOpen = () => setConnState('open');
    const onError = () => setConnState('lost');
    es.addEventListener('webhook', onMessage as EventListener);
    es.addEventListener('open', onOpen);
    es.addEventListener('error', onError);
    return () => {
      es.removeEventListener('webhook', onMessage as EventListener);
      es.removeEventListener('open', onOpen);
      es.removeEventListener('error', onError);
      es.close();
    };
  }, []);

  const visibleEvents = useMemo(() => {
    if (submittedAt === null) return eventEntries;
    const cutoff = new Date(submittedAt).getTime();
    return eventEntries.filter((e) => {
      const t = new Date(e.receivedAt).getTime();
      return Number.isFinite(t) && t >= cutoff;
    });
  }, [eventEntries, submittedAt]);

  // Auto-switch to «События» tab when the first post-submit event lands —
  // signals to the user that registration succeeded and something
  // happened in the registry.
  const prevEventCountRef = useRef<number>(0);
  useEffect(() => {
    if (visibleEvents.length > 0 && prevEventCountRef.current === 0 && submittedAt !== null) {
      setTab('events');
    }
    prevEventCountRef.current = visibleEvents.length;
  }, [visibleEvents.length, submittedAt]);

  const previewBody = useMemo(() => {
    if (dealTemplate === null) return null;
    const addrA = partyAddrs?.addrA ?? '<address_A_pending>';
    const addrB = partyAddrs?.addrB ?? '<address_B_pending>';
    const amount = form.amount === '' ? '<сумма>' : form.amount;
    return buildGatewayBody({
      template: dealTemplate,
      addrA,
      addrB,
      amount,
      executeAt: form.executeAt,
      attachments: form.attachments,
    });
  }, [dealTemplate, partyAddrs, form.amount, form.executeAt, form.attachments]);

  return (
    <Card className="lg:sticky lg:top-6 lg:max-h-[calc(100vh-3rem)] lg:overflow-y-auto">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-sm">Структура договора</CardTitle>
          <ConnStateBadge state={connState} />
        </div>
        <CardDescription className="text-xs">
          Так договор будет зарегистрирован в распределённом реестре. Обновляется по мере заполнения
          формы.
        </CardDescription>
      </CardHeader>
      <div className="border-b border-slate-200">
        <div className="flex px-3 gap-1" role="tablist">
          <SideTabButton
            active={tab === 'structure'}
            onClick={() => setTab('structure')}
            label="Структура"
          />
          <SideTabButton active={tab === 'json'} onClick={() => setTab('json')} label="JSON" />
          <SideTabButton
            active={tab === 'events'}
            onClick={() => setTab('events')}
            label="События"
            badge={visibleEvents.length > 0 ? visibleEvents.length : null}
          />
        </div>
      </div>
      <CardContent className="text-sm pt-4">
        {tab === 'structure' && (
          <StructureView
            dealTemplate={dealTemplate}
            buyer={buyer}
            seller={seller}
            amount={form.amount}
            executeAt={form.executeAt}
            attachments={form.attachments}
          />
        )}
        {tab === 'json' && <JsonView previewBody={previewBody} />}
        {tab === 'events' && <EventsView visibleEvents={visibleEvents} submittedAt={submittedAt} />}
      </CardContent>
    </Card>
  );
}

interface SideTabButtonProps {
  readonly active: boolean;
  readonly onClick: () => void;
  readonly label: string;
  readonly badge?: number | null;
}

function SideTabButton({ active, onClick, label, badge }: SideTabButtonProps) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'px-3 py-2 text-xs font-medium border-b-2 -mb-px transition-colors',
        active
          ? 'border-brand text-brand'
          : 'border-transparent text-slate-500 hover:text-slate-900',
      )}
    >
      <span>{label}</span>
      {badge !== null && badge !== undefined && (
        <span className="ml-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold">
          {badge}
        </span>
      )}
    </button>
  );
}

// ── «Структура» tab ──

interface StructureViewProps {
  readonly dealTemplate: DealTemplate | null;
  readonly buyer: MockCounterparty | null;
  readonly seller: MockCounterparty | null;
  readonly amount: string;
  readonly executeAt: string;
  readonly attachments: readonly AttachmentSnapshot[];
}

function StructureView({
  dealTemplate,
  buyer,
  seller,
  amount,
  executeAt,
  attachments,
}: StructureViewProps) {
  if (dealTemplate === null) {
    return (
      <div className="text-xs text-slate-500 py-4 text-center">
        Выберите тип сделки на шаге 1 — структура договора появится здесь.
      </div>
    );
  }
  const buyerLabel = buyer?.name ?? 'покупатель (не выбран)';
  const sellerLabel = seller?.name ?? 'продавец (не выбран)';
  const amountLabel = amount === '' ? '—' : formatAmount(amount);
  const executeAtLabel = executeAt === '' ? 'не указана' : formatTimestamp(executeAt);

  const agreements = describeAgreements({
    templateId: dealTemplate.id,
    buyerLabel,
    sellerLabel,
    amount,
    executeAt,
  });

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-1.5">
        <div className="text-xs text-slate-500">Тип договора:</div>
        <div className="font-semibold text-slate-900">{dealTemplate.title}</div>
      </div>
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
          Стороны:
        </div>
        <div className="text-xs space-y-1">
          <div>
            <span className="text-slate-500">Покупатель:</span>{' '}
            <span className="text-slate-900 font-medium">{buyerLabel}</span>
          </div>
          <div>
            <span className="text-slate-500">Продавец:</span>{' '}
            <span className="text-slate-900 font-medium">{sellerLabel}</span>
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
          {agreements.length === 1 ? 'Соглашение:' : `Соглашения (${agreements.length}):`}
        </div>
        <ul className="space-y-2">
          {agreements.map((a, idx) => (
            <li
              key={`${a.title}-${idx}`}
              className="rounded-md border border-slate-200 bg-white p-2.5 space-y-0.5"
            >
              <div className="text-xs font-semibold text-slate-900">
                {idx + 1}. {a.title}
              </div>
              <div className="text-xs text-slate-600">{a.direction}</div>
              <div className="text-xs">
                <span className="text-slate-500">Сумма:</span>{' '}
                <strong className="text-slate-900">{a.amount}</strong>
              </div>
              <div className="text-xs">
                <span className="text-slate-500">Когда:</span>{' '}
                <span className="text-slate-700">{a.when}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
      {dealTemplate.requiresDate && (
        <div className="text-xs">
          <span className="text-slate-500">Срок исполнения:</span>{' '}
          <span className="text-slate-700">{executeAtLabel}</span>
        </div>
      )}
      {attachments.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">
            Документы:
          </div>
          <ul className="text-xs space-y-1">
            {attachments.map((a, idx) => (
              <li key={`${a.fileHash}-${idx}`} className="flex items-center gap-1.5">
                <span>📎</span>
                <span className="text-slate-900 truncate">{a.filename}</span>
                <span className="text-slate-500">({formatBytes(a.sizeBytes)})</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="text-[10px] text-slate-400 pt-2 border-t border-slate-100">
        Сумма «{amountLabel}» — заполняется на шаге «Сумма и срок».
      </div>
    </div>
  );
}

interface AgreementDesc {
  readonly title: string;
  readonly direction: string;
  readonly amount: string;
  readonly when: string;
}

function describeAgreements(args: {
  templateId: DealTemplate['id'];
  buyerLabel: string;
  sellerLabel: string;
  amount: string;
  executeAt: string;
}): AgreementDesc[] {
  const { templateId, buyerLabel, sellerLabel, amount, executeAt } = args;
  const amountLabel = amount === '' ? '—' : formatAmount(amount);
  const when = executeAt === '' ? 'немедленно после подписания' : formatTimestamp(executeAt);
  const buyToSell = `${buyerLabel} → ${sellerLabel}`;
  const sellToBuy = `${sellerLabel} → ${buyerLabel}`;
  switch (templateId) {
    case 'immediate-transfer':
      return [
        {
          title: 'Перевод средств',
          direction: buyToSell,
          amount: amountLabel,
          when: 'немедленно после подписания',
        },
      ];
    case 'gift':
      return [
        {
          title: 'Дарение (безвозмездный перевод)',
          direction: buyToSell,
          amount: amountLabel,
          when: 'немедленно после подписания',
        },
      ];
    case 'scheduled-payment':
      return [{ title: 'Платёж по сроку', direction: buyToSell, amount: amountLabel, when }];
    case 'delivery-with-repayment': {
      const half =
        amount !== '' && Number.isFinite(Number.parseFloat(amount))
          ? formatAmount((Number.parseFloat(amount) * 0.5).toFixed(0))
          : '—';
      return [
        {
          title: 'Аванс',
          direction: buyToSell,
          amount: amountLabel,
          when: 'немедленно после подписания',
        },
        {
          title: 'Возврат части по поставке',
          direction: sellToBuy,
          amount: half,
          when,
        },
      ];
    }
    case 'delivery-with-prepayment-guarantee':
      return [
        {
          title: 'Предоплата',
          direction: buyToSell,
          amount: amountLabel,
          when: 'немедленно после подписания',
        },
        {
          title: 'Обратное обязательство (гарантия возврата)',
          direction: sellToBuy,
          amount: amountLabel,
          when: `${when} — если поставка не зафиксирована актом`,
        },
      ];
    case 'delivery-post-payment':
      return [
        { title: 'Поставка товара', direction: sellToBuy, amount: amountLabel, when },
        { title: 'Оплата по факту поставки', direction: buyToSell, amount: amountLabel, when },
      ];
    default:
      return [];
  }
}

// ── «JSON» tab ──

interface JsonViewProps {
  readonly previewBody: Record<string, unknown> | null;
}

function JsonView({ previewBody }: JsonViewProps) {
  if (previewBody === null) {
    return (
      <div className="text-xs text-slate-500 py-4 text-center">
        Выберите тип сделки — здесь появится тело запроса к Gateway в формате JSON.
      </div>
    );
  }
  const pretty = JSON.stringify(previewBody, null, 2);
  return (
    <div className="space-y-2">
      <div className="text-[10px] text-slate-500 bg-slate-50 border border-slate-200 rounded p-2">
        Это <strong>тело HTTP-запроса</strong> к Gateway. Подпись Ed25519 будет добавлена
        автоматически при отправке. Финальная запись в реестре содержит дополнительно: txId,
        block_number, timestamp.
      </div>
      <pre className="text-[11px] font-mono leading-snug bg-slate-900 text-slate-100 rounded-md p-3 overflow-x-auto whitespace-pre-wrap break-all">
        {pretty}
      </pre>
    </div>
  );
}

// ── «События» tab ──

interface EventsViewProps {
  readonly visibleEvents: readonly WebhookEntry[];
  readonly submittedAt: string | null;
}

function EventsView({ visibleEvents, submittedAt }: EventsViewProps) {
  if (submittedAt === null) {
    return (
      <div className="text-xs text-slate-500 py-4 text-center">
        События появятся после отправки договора. Создайте договор на шаге 6 — лента активируется
        автоматически.
      </div>
    );
  }
  if (visibleEvents.length === 0) {
    return (
      <div className="text-xs text-slate-500 py-4 text-center">
        Ожидание событий от реестра…
        <div className="text-[10px] mt-1 text-slate-400">Обычно занимает менее секунды.</div>
      </div>
    );
  }
  return (
    <ol className="space-y-2">
      {visibleEvents.map((entry) => (
        <BusinessTimelineEntry key={entry.id} entry={entry} />
      ))}
    </ol>
  );
}

interface StepDealTypeProps {
  readonly selectedId: DealTemplate['id'] | null;
  readonly onSelect: (id: DealTemplate['id']) => void;
}

function StepDealType({ selectedId, onSelect }: StepDealTypeProps) {
  return (
    <div className="grid gap-3 sm:grid-cols-1">
      {DEAL_TEMPLATES.map((template) => {
        const active = selectedId === template.id;
        return (
          <button
            key={template.id}
            type="button"
            onClick={() => onSelect(template.id)}
            aria-pressed={active}
            className={cn(
              'text-left rounded-lg border-2 p-4 transition-colors',
              active
                ? 'border-brand bg-brand/5'
                : 'border-slate-200 hover:border-slate-300 hover:bg-slate-50',
            )}
          >
            <div className="flex items-center justify-between">
              <strong className="text-sm text-slate-900">{template.title}</strong>
              {active && <Badge variant="info">выбрано</Badge>}
            </div>
            <p className="text-xs text-slate-600 mt-1.5">{template.summary}</p>
            <div className="mt-2.5 text-xs text-slate-500">
              <div className="font-medium text-slate-600 mb-1">Этапы:</div>
              <ol className="space-y-0.5 pl-4 list-decimal">
                {template.stages.map((stage) => (
                  <li key={stage}>{stage}</li>
                ))}
              </ol>
            </div>
          </button>
        );
      })}
    </div>
  );
}

interface StepCounterpartyProps {
  readonly role: 'buyer' | 'seller';
  readonly counterparties: readonly MockCounterparty[];
  readonly selectedId: string | null;
  readonly excludeId: string | null;
  readonly onSelect: (id: string) => void;
}

function StepCounterparty({
  role,
  counterparties,
  selectedId,
  excludeId,
  onSelect,
}: StepCounterpartyProps) {
  const roleLabel = role === 'buyer' ? 'покупателя' : 'продавца';
  return (
    <div className="space-y-3">
      <p className="text-sm text-slate-600">
        Выберите {roleLabel} из списка зарегистрированных контрагентов:
      </p>
      <div className="grid gap-2">
        {counterparties.map((c) => {
          const excluded = excludeId === c.id;
          const active = selectedId === c.id;
          return (
            <button
              key={c.id}
              type="button"
              disabled={excluded}
              onClick={() => onSelect(c.id)}
              aria-pressed={active}
              className={cn(
                'text-left rounded-md border p-3 transition-colors',
                excluded && 'opacity-40 cursor-not-allowed border-slate-200',
                !excluded && active && 'border-brand bg-brand/5',
                !excluded && !active && 'border-slate-200 hover:border-slate-300 hover:bg-slate-50',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium text-sm text-slate-900 truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 truncate">{c.hint}</div>
                </div>
                {excluded && <span className="text-xs text-slate-400 shrink-0">уже выбрано</span>}
                {active && !excluded && <Badge variant="info">выбрано</Badge>}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

interface StepAmountDateProps {
  readonly template: DealTemplate;
  readonly amount: string;
  readonly executeAt: string;
  readonly onAmountChange: (amount: string) => void;
  readonly onDateChange: (executeAt: string) => void;
}

function StepAmountDate({
  template,
  amount,
  executeAt,
  onAmountChange,
  onDateChange,
}: StepAmountDateProps) {
  const amountValid = amount === '' || /^\d+(\.\d{1,2})?$/.test(amount);
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="business-demo-amount">Сумма сделки (в рублях)</Label>
        <Input
          id="business-demo-amount"
          type="text"
          inputMode="decimal"
          placeholder="например, 100000"
          value={amount}
          onChange={(e) => onAmountChange(e.target.value)}
          className={!amountValid ? 'border-red-300 focus:ring-red-400 focus:border-red-400' : ''}
        />
        {!amountValid && (
          <p className="text-xs text-red-600 mt-1">
            Введите целое число или десятичную дробь (например: 100000 или 100000.50).
          </p>
        )}
        {amount !== '' && amountValid && (
          <p className="text-xs text-slate-600 mt-1">
            Будет зарегистрировано: <strong>{formatAmount(amount)}</strong>
          </p>
        )}
      </div>
      {template.requiresDate && (
        <div>
          <Label htmlFor="business-demo-date">Дата исполнения</Label>
          <Input
            id="business-demo-date"
            type="datetime-local"
            value={executeAt}
            onChange={(e) => onDateChange(e.target.value)}
          />
          <p className="text-xs text-slate-500 mt-1">
            Сделка типа «{template.title}» требует указания даты будущего исполнения. До этой даты
            обязательство в реестре будет в статусе «ожидает исполнения».
          </p>
        </div>
      )}
      {!template.requiresDate && (
        <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
          Сделка типа «{template.title}» исполняется немедленно — указывать дату не требуется.
        </div>
      )}
    </div>
  );
}

interface StepAttachmentsProps {
  readonly attachments: readonly AttachmentSnapshot[];
  readonly onAdd: (a: AttachmentSnapshot) => void;
  readonly onRemove: (index: number) => void;
}

type UploadState =
  | { kind: 'idle' }
  | { kind: 'uploading'; filename: string }
  | { kind: 'error'; message: string };

function StepAttachments({ attachments, onAdd, onRemove }: StepAttachmentsProps) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadState, setUploadState] = useState<UploadState>({ kind: 'idle' });
  const [pendingDescription, setPendingDescription] = useState<string>('');
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  const onChoose = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setPendingFile(file);
    setUploadState({ kind: 'idle' });
  };

  const onConfirmUpload = async () => {
    if (pendingFile === null) return;
    setUploadState({ kind: 'uploading', filename: pendingFile.name });
    try {
      const res = await uploadFile(pendingFile);
      if (!res.ok) {
        setUploadState({ kind: 'error', message: res.error.message });
        return;
      }
      onAdd({
        fileHash: res.result.fileHash,
        mime: res.result.mime,
        sizeBytes: res.result.sizeBytes,
        filename: pendingFile.name,
        description: pendingDescription.trim(),
      });
      setPendingFile(null);
      setPendingDescription('');
      setUploadState({ kind: 'idle' });
      if (fileInputRef.current !== null) {
        fileInputRef.current.value = '';
      }
    } catch (e) {
      setUploadState({
        kind: 'error',
        message: `Не удалось загрузить файл: ${(e as Error).message}`,
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600">
        Загрузите PDF, JPG, PNG, JSON или другой документ к договору (например, акт, счёт или
        спецификацию). Файлы хранятся в защищённом DLT-хранилище. Можно пропустить этот шаг.
      </div>

      {attachments.length > 0 && (
        <div className="space-y-2">
          <Label className="text-xs uppercase tracking-wider text-slate-500">
            Приложено ({attachments.length}):
          </Label>
          <ul className="space-y-2">
            {attachments.map((a, idx) => (
              <li
                key={`${a.fileHash}-${idx}`}
                className="flex items-start gap-3 rounded-md border border-slate-200 bg-white p-3"
              >
                <span className="text-xl leading-none mt-0.5">📎</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{a.filename}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {a.mime} · {formatBytes(a.sizeBytes)}
                  </div>
                  {a.description.length > 0 && (
                    <div className="text-xs text-slate-700 mt-1">{a.description}</div>
                  )}
                  <code className="text-[10px] text-slate-400 font-mono break-all block mt-1">
                    {a.fileHash}
                  </code>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemove(idx)}
                  aria-label="Удалить файл"
                >
                  ✕
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="rounded-md border-2 border-dashed border-slate-300 p-4 space-y-3">
        <div>
          <Label htmlFor="business-demo-file">Выберите файл (≤ 50 MiB)</Label>
          <input
            ref={fileInputRef}
            id="business-demo-file"
            type="file"
            accept="application/pdf,image/png,image/jpeg,application/json,text/plain"
            onChange={onChoose}
            className="block w-full text-sm text-slate-700 mt-1 file:mr-3 file:py-1.5 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 cursor-pointer"
          />
        </div>
        {pendingFile !== null && (
          <>
            <div className="text-xs text-slate-600">
              Выбран: <strong>{pendingFile.name}</strong> ({formatBytes(pendingFile.size)})
            </div>
            <div>
              <Label htmlFor="business-demo-description">Описание (опционально)</Label>
              <Input
                id="business-demo-description"
                type="text"
                placeholder="например, акт приёмки от 03.06.2026"
                value={pendingDescription}
                onChange={(e) => setPendingDescription(e.target.value)}
              />
            </div>
            <Button
              onClick={() => void onConfirmUpload()}
              disabled={uploadState.kind === 'uploading'}
              size="sm"
            >
              {uploadState.kind === 'uploading'
                ? 'Загружается…'
                : 'Загрузить и приложить к договору'}
            </Button>
          </>
        )}
        {uploadState.kind === 'error' && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2">
            <strong>Ошибка загрузки:</strong> {uploadState.message}
          </div>
        )}
      </div>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} Б`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} КиБ`;
  return `${(n / (1024 * 1024)).toFixed(2)} МиБ`;
}

interface StepConfirmProps {
  readonly template: DealTemplate;
  readonly buyer: MockCounterparty;
  readonly seller: MockCounterparty;
  readonly amount: string;
  readonly executeAt: string;
  readonly attachments: readonly AttachmentSnapshot[];
  readonly submit: SubmitState;
  readonly onSubmit: () => void;
  readonly onReset: () => void;
  readonly contractIdFromSubmit: string | null;
  readonly partyAddrs: { addrA: string; addrB: string } | null;
}

function StepConfirm({
  template,
  buyer,
  seller,
  amount,
  executeAt,
  attachments,
  submit,
  onSubmit,
  onReset,
  contractIdFromSubmit,
  partyAddrs,
}: StepConfirmProps) {
  const isComposite = extractIsComposite(template);
  // Sandbox pre-fund preview — match `onSubmit` exactly so the banner
  // describes what the wizard is about to do, not a guess. Wraps in
  // try/catch because `buildGatewayBody` is shared with previews that
  // tolerate missing fields, but `computeBuyerPreMint` does throw on
  // malformed `amount` strings (>2 fractional digits) — Step 4
  // validation already prevents that, so the throw is unreachable here.
  const preMintAmount = useMemo(() => {
    if (partyAddrs === null || amount === '') return null;
    try {
      const body = buildGatewayBody({
        template,
        addrA: partyAddrs.addrA,
        addrB: partyAddrs.addrB,
        amount,
        executeAt,
        attachments,
      });
      return computeBuyerPreMint(body, partyAddrs.addrA);
    } catch {
      return null;
    }
  }, [template, partyAddrs, amount, executeAt, attachments]);
  return (
    <div className="space-y-4">
      <div className="rounded-md border border-slate-200 bg-slate-50 p-4 space-y-2">
        <ConfirmRow label="Тип сделки" value={template.title} />
        <ConfirmRow label="Покупатель" value={`${buyer.name} (${buyer.hint})`} />
        <ConfirmRow label="Продавец" value={`${seller.name} (${seller.hint})`} />
        <ConfirmRow label="Сумма" value={formatAmount(amount)} highlight />
        {template.requiresDate && (
          <ConfirmRow label="Срок исполнения" value={formatTimestamp(executeAt) || executeAt} />
        )}
        {attachments.length > 0 && (
          <ConfirmRow
            label="Документы"
            value={`${attachments.length} ${pluralizeDocuments(attachments.length)}`}
          />
        )}
      </div>
      {submit.kind === 'idle' && preMintAmount !== null && (
        <div className="rounded-md border border-sky-200 bg-sky-50 p-3 text-xs text-sky-900">
          <div className="font-medium mb-0.5">Sandbox: автоматическое пополнение баланса</div>
          <div>
            Перед регистрацией договора эмулятор выпустит{' '}
            <strong>{formatAmount(preMintAmount)}</strong> на адрес покупателя — это позволяет демо
            работать в чистой песочнице, где балансы пустые после редеплоя. В production-интеграциях
            OP пополняет адреса самостоятельно.
          </div>
        </div>
      )}
      {submit.kind === 'idle' && (
        <div className="flex gap-3">
          <Button onClick={onSubmit} size="lg">
            Создать договор
          </Button>
          <Button variant="ghost" onClick={onReset}>
            Начать заново
          </Button>
        </div>
      )}
      {submit.kind === 'submitting' && (
        <div className="flex items-center gap-3 text-sm text-slate-700">
          <span className="size-3 rounded-full bg-sky-500 animate-pulse" />
          Подписание и регистрация в реестре…
        </div>
      )}
      {submit.kind === 'success' && <SubmitSuccessBlock result={submit.result} onReset={onReset} />}
      {submit.kind === 'error' && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-900">
          <div className="font-medium mb-1">Не удалось зарегистрировать договор</div>
          <div className="text-xs">{submit.message}</div>
          <Button className="mt-3" variant="secondary" onClick={onReset}>
            Попробовать снова
          </Button>
        </div>
      )}
      {submit.kind === 'success' &&
        isComposite &&
        contractIdFromSubmit !== null &&
        partyAddrs !== null && (
          <ActSubmitPanel
            contractId={contractIdFromSubmit}
            template={template}
            partyAddrs={partyAddrs}
            maxAmount={amount}
          />
        )}
    </div>
  );
}

function pluralizeDocuments(n: number): string {
  if (n % 10 === 1 && n % 100 !== 11) return 'документ';
  if (n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14)) return 'документа';
  return 'документов';
}

interface ConfirmRowProps {
  readonly label: string;
  readonly value: string;
  readonly highlight?: boolean;
}

function ConfirmRow({ label, value, highlight }: ConfirmRowProps) {
  return (
    <div className="grid grid-cols-[140px_1fr] gap-3 text-sm">
      <span className="text-slate-500">{label}:</span>
      <span className={cn('text-slate-900', highlight && 'font-semibold')}>{value}</span>
    </div>
  );
}

// ── Acts UI (Phase F B-NEW-4) ───────────────────────────────────

interface ActSubmitPanelProps {
  readonly contractId: string;
  readonly template: DealTemplate;
  readonly partyAddrs: { addrA: string; addrB: string };
  readonly maxAmount: string;
}

type ActSubmitState =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | {
      kind: 'success';
      actId: string | null;
      amount: string;
      description: string;
      attachmentCount: number;
      submittedAt: string;
    }
  | { kind: 'error'; message: string };

/**
 * «Зафиксировать поставку» — wire'd to `POST /contract/{id}/act`
 * (chaincode AddDeliveryAct). Available only on composite contracts
 * (Patent §8.4 prepayment-with-guarantee, §8.5 post-payment) where
 * incremental act submissions are how the contract actually closes.
 *
 * Backend auto-signs the act body using EMULATOR_PRIV_A/_B (same
 * pipeline as case/contract). Frontend just supplies amount,
 * description, and optional attachments — the canonical signing
 * payload (`packages/shared/src/crypto/act-format.ts`) is built
 * server-side.
 */
function ActSubmitPanel({ contractId, template, partyAddrs, maxAmount }: ActSubmitPanelProps) {
  const [submittedActs, setSubmittedActs] = useState<
    Array<{ readonly id: string | null; readonly amount: string; readonly at: string }>
  >([]);
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [actAttachments, setActAttachments] = useState<AttachmentSnapshot[]>([]);
  const [state, setState] = useState<ActSubmitState>({ kind: 'idle' });

  const amountValid = amount === '' || /^\d+(\.\d{1,2})?$/.test(amount);

  const onSubmit = async () => {
    if (!amountValid || amount === '') return;
    setState({ kind: 'submitting' });
    try {
      const wireAttachments =
        actAttachments.length > 0
          ? actAttachments.map((a) => ({
              file_hash: a.fileHash,
              mime: a.mime,
              ...(a.description.length > 0 ? { description: a.description } : {}),
            }))
          : undefined;
      const result = await callProxy('act_submit', {
        pathParams: { id: contractId },
        body: {
          amount,
          ...(description.length > 0 ? { description } : {}),
          parties: [partyAddrs.addrA, partyAddrs.addrB],
          ...(wireAttachments !== undefined ? { attachments: wireAttachments } : {}),
        },
      });
      if (result.proxy_status !== 'ok') {
        setState({
          kind: 'error',
          message:
            'error_kind' in result
              ? `Ошибка связи с реестром: ${result.error_message}`
              : 'Неизвестная ошибка',
        });
        return;
      }
      const isAccepted = result.gateway_status_code >= 200 && result.gateway_status_code < 300;
      if (!isAccepted) {
        const body = result.gateway_body as { error?: { message?: string } } | null;
        setState({
          kind: 'error',
          message: `Реестр отклонил акт (HTTP ${result.gateway_status_code}): ${
            body?.error?.message ?? 'без подробностей'
          }`,
        });
        return;
      }
      const ok = result.gateway_body as { act_id?: unknown } | null;
      const actId = typeof ok?.act_id === 'string' ? ok.act_id : null;
      const submittedAt = new Date().toISOString();
      setSubmittedActs((prev) => [{ id: actId, amount, at: submittedAt }, ...prev]);
      setState({
        kind: 'success',
        actId,
        amount,
        description,
        attachmentCount: actAttachments.length,
        submittedAt,
      });
      setAmount('');
      setDescription('');
      setActAttachments([]);
    } catch (e) {
      setState({ kind: 'error', message: (e as Error).message });
    }
  };

  return (
    <div className="rounded-md border border-sky-200 bg-sky-50 p-4 space-y-3">
      <div className="flex items-start gap-3">
        <span className="text-2xl leading-none">🔄</span>
        <div className="flex-1">
          <h3 className="text-sm font-semibold text-slate-900">Зафиксировать поставку</h3>
          <p className="text-xs text-slate-600 mt-0.5">
            Для договора типа «{template.title}» исполнение фиксируется через акты поставки.
            Регистрация акта в реестре снижает оставшееся обязательство.
          </p>
        </div>
      </div>
      <div className="space-y-3">
        <div>
          <Label htmlFor="act-amount">Сумма по акту (₽)</Label>
          <Input
            id="act-amount"
            type="text"
            inputMode="decimal"
            placeholder={`не более ${formatAmount(maxAmount)}`}
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className={!amountValid ? 'border-red-300' : ''}
          />
          {!amountValid && (
            <p className="text-xs text-red-600 mt-1">Введите число (например: 1500 или 1500.50).</p>
          )}
        </div>
        <div>
          <Label htmlFor="act-description">Описание (опционально)</Label>
          <Input
            id="act-description"
            type="text"
            placeholder="например, поставка партии №1 от 03.06.2026"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
        <ActAttachmentsControl
          attachments={actAttachments}
          onAdd={(a) => setActAttachments((prev) => [...prev, a])}
          onRemove={(idx) => setActAttachments((prev) => prev.filter((_, i) => i !== idx))}
        />
        <div className="flex items-center gap-3">
          <Button
            onClick={() => void onSubmit()}
            disabled={!amountValid || amount === '' || state.kind === 'submitting'}
            size="sm"
          >
            {state.kind === 'submitting' ? 'Регистрация…' : 'Зафиксировать акт'}
          </Button>
          {state.kind === 'success' && (
            <span className="text-xs text-emerald-700">
              ✅ Акт зарегистрирован {state.actId !== null ? `(${state.actId})` : ''}
            </span>
          )}
          {state.kind === 'error' && <span className="text-xs text-red-700">{state.message}</span>}
        </div>
      </div>
      {submittedActs.length > 0 && (
        <div className="pt-3 border-t border-sky-200">
          <div className="text-xs font-medium text-slate-600 mb-1.5">
            Зафиксированные акты ({submittedActs.length}):
          </div>
          <ul className="space-y-1 text-xs">
            {submittedActs.map((a, idx) => (
              <li key={`${a.id ?? 'noid'}-${idx}`} className="flex items-center gap-2">
                <span className="text-emerald-600">●</span>
                <span className="text-slate-900 font-medium">{formatAmount(a.amount)}</span>
                {a.id !== null && (
                  <code className="text-[10px] text-slate-400 font-mono">{a.id}</code>
                )}
                <time className="text-slate-500" dateTime={a.at}>
                  {formatTimestamp(a.at)}
                </time>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

interface ActAttachmentsControlProps {
  readonly attachments: readonly AttachmentSnapshot[];
  readonly onAdd: (a: AttachmentSnapshot) => void;
  readonly onRemove: (idx: number) => void;
}

function ActAttachmentsControl({ attachments, onAdd, onRemove }: ActAttachmentsControlProps) {
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploadingName, setUploadingName] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const onUpload = async (file: File) => {
    setUploadingName(file.name);
    setErr(null);
    const res = await uploadFile(file);
    setUploadingName(null);
    if (!res.ok) {
      setErr(res.error.message);
      return;
    }
    onAdd({
      fileHash: res.result.fileHash,
      mime: res.result.mime,
      sizeBytes: res.result.sizeBytes,
      filename: file.name,
      description: '',
    });
    setPendingFile(null);
    if (inputRef.current !== null) inputRef.current.value = '';
  };

  return (
    <div className="space-y-2">
      <Label className="text-xs">Документы к акту (опционально)</Label>
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="file"
          accept="application/pdf,image/png,image/jpeg,application/json,text/plain"
          onChange={(e) => setPendingFile(e.target.files?.[0] ?? null)}
          className="block text-xs text-slate-700 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 cursor-pointer"
        />
        {pendingFile !== null && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => void onUpload(pendingFile)}
            disabled={uploadingName !== null}
          >
            {uploadingName !== null ? '…' : 'Прикрепить'}
          </Button>
        )}
      </div>
      {err !== null && <div className="text-xs text-red-700">{err}</div>}
      {attachments.length > 0 && (
        <ul className="space-y-1 text-xs">
          {attachments.map((a, idx) => (
            <li
              key={`${a.fileHash}-${idx}`}
              className="flex items-center gap-2 bg-white border border-slate-200 rounded px-2 py-1"
            >
              <span>📎</span>
              <span className="flex-1 truncate text-slate-700">{a.filename}</span>
              <Button variant="ghost" size="sm" onClick={() => onRemove(idx)} aria-label="Удалить">
                ✕
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface SubmitSuccessBlockProps {
  readonly result: ProxyResult;
  readonly onReset: () => void;
}

function SubmitSuccessBlock({ result, onReset }: SubmitSuccessBlockProps) {
  if (result.proxy_status !== 'ok') {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <div className="font-medium mb-1">Возникла техническая ошибка</div>
        <div className="text-xs">
          {'error_kind' in result ? `${result.error_kind}: ${result.error_message}` : 'unknown'}
        </div>
        <Button className="mt-3" variant="secondary" onClick={onReset}>
          Попробовать снова
        </Button>
      </div>
    );
  }
  const gw = result.gateway_body as {
    agreement_id?: string;
    contract_id?: string;
    agreement_ids?: readonly string[];
  } | null;
  const agreementId = gw?.agreement_id ?? gw?.agreement_ids?.[0] ?? null;
  const contractId = gw?.contract_id ?? null;
  const isAccepted = result.gateway_status_code >= 200 && result.gateway_status_code < 300;
  if (!isAccepted) {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <div className="font-medium mb-1">Реестр отклонил запрос</div>
        <div className="text-xs">
          Код ответа: {result.gateway_status_code}. Подробности доступны в режиме разработчика.
        </div>
        <Button className="mt-3" variant="secondary" onClick={onReset}>
          Создать новый договор
        </Button>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
      <div className="font-medium mb-2 flex items-center gap-2">
        <span className="text-lg">✅</span> Договор зарегистрирован в реестре
      </div>
      <div className="space-y-1 text-xs">
        {agreementId !== null && (
          <div>
            <span className="text-emerald-700">Идентификатор обязательства:</span>{' '}
            <code className="font-mono text-emerald-900">{agreementId}</code>
          </div>
        )}
        {contractId !== null && (
          <div>
            <span className="text-emerald-700">Идентификатор договора:</span>{' '}
            <code className="font-mono text-emerald-900">{contractId}</code>
          </div>
        )}
        <div className="pt-1.5 text-emerald-800">
          Лента событий ниже покажет следующие шаги обработки сделки в режиме реального времени.
        </div>
      </div>
      <Button className="mt-3" variant="secondary" onClick={onReset}>
        Создать новый договор
      </Button>
    </div>
  );
}

interface BusinessTimelineEntryProps {
  readonly entry: WebhookEntry;
}

function BusinessTimelineEntry({ entry }: BusinessTimelineEntryProps) {
  const display = translateEventType(entry.eventType);
  const toneBorder = {
    success: 'border-emerald-300 bg-emerald-50',
    info: 'border-sky-300 bg-sky-50',
    warning: 'border-amber-300 bg-amber-50',
    danger: 'border-red-300 bg-red-50',
  }[display.tone];
  const body = entry.parsedBody as {
    amount?: string | number;
    spec_hash?: string;
    block_number?: number;
  } | null;
  return (
    <li className={cn('rounded-md border-l-4 px-4 py-3', toneBorder)}>
      <div className="flex items-start gap-3">
        <span className="text-2xl leading-none mt-0.5" aria-hidden="true">
          {display.icon}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <strong className="text-sm text-slate-900">{display.title}</strong>
            <time className="text-xs text-slate-500" dateTime={entry.receivedAt}>
              {formatTimestamp(entry.receivedAt)}
            </time>
          </div>
          <p className="text-xs text-slate-700 mt-1">{display.explanation}</p>
          {body?.amount !== undefined && (
            <div className="text-xs text-slate-600 mt-1">
              Сумма: <strong>{formatAmount(body.amount)}</strong>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

interface ConnStateBadgeProps {
  readonly state: 'connecting' | 'open' | 'lost';
}

function ConnStateBadge({ state }: ConnStateBadgeProps) {
  if (state === 'connecting') {
    return (
      <Badge variant="muted" title="Подключение к ленте событий">
        соединение…
      </Badge>
    );
  }
  if (state === 'lost') {
    return (
      <Badge variant="danger" title="Лента отключилась. Обновите страницу.">
        нет связи
      </Badge>
    );
  }
  return (
    <Badge variant="success" title="Лента активна">
      <span className="inline-flex items-center gap-1.5">
        <span className="size-1.5 rounded-full bg-emerald-500 animate-pulse" />в эфире
      </span>
    </Badge>
  );
}

// ── Gateway-body builders ───────────────────────────────────────

interface BuildBodyArgs {
  readonly template: DealTemplate;
  readonly addrA: string;
  readonly addrB: string;
  readonly amount: string;
  readonly executeAt: string;
  readonly attachments?: readonly AttachmentSnapshot[];
}

/**
 * Phase F preview-mode body builder: same as `buildGatewayBody`, but
 * accepts placeholder addresses so the right-panel «Структура» / «JSON»
 * tab can render before `/api/party-addresses` has resolved. Pure +
 * idempotent — identical to the submit-path output when `partyAddrs`
 * is supplied.
 */
export function buildGatewayBodyPreview(args: BuildBodyArgs): Record<string, unknown> {
  return buildGatewayBody(args);
}

function extractContractId(result: ProxyResult): string | null {
  if (result.proxy_status !== 'ok') return null;
  const body = result.gateway_body as { contract_id?: unknown } | null;
  return typeof body?.contract_id === 'string' ? body.contract_id : null;
}

function extractIsComposite(template: DealTemplate | null): boolean {
  return (
    template !== null &&
    (template.id === 'delivery-with-repayment' ||
      template.id === 'delivery-with-prepayment-guarantee' ||
      template.id === 'delivery-post-payment')
  );
}

/**
 * Per-run nonce embedded в agreement IDs to avoid spec_hash collisions
 * между последовательными submit'ами (memory backend is in-process
 * stateful). Mirror of `tools/op-emulator/src/scenarios/full-flow-16-step.ts`
 * pattern after Issue #21 fix.
 */
function freshNonce(): string {
  return Date.now().toString(36) + '-' + Math.floor(Math.random() * 1e6).toString(36);
}

function buildGatewayBody({
  template,
  addrA,
  addrB,
  amount,
  executeAt,
  attachments,
}: BuildBodyArgs): Record<string, unknown> {
  const runNonce = freshNonce();
  const executeAtIso = executeAt === '' ? 'now' : new Date(executeAt).toISOString();
  // Phase F: map UI AttachmentSnapshot → Gateway wire shape (`Attachment`
  // from schemas/common.ts). Only emit the field when non-empty; an
  // empty array is harmless but adds visual noise to the JSON preview.
  const wireAttachments =
    attachments !== undefined && attachments.length > 0
      ? attachments.map((a) => ({
          file_hash: a.fileHash,
          mime: a.mime,
          ...(a.description.length > 0 ? { description: a.description } : {}),
        }))
      : undefined;
  if (
    template.id === 'immediate-transfer' ||
    template.id === 'scheduled-payment' ||
    template.id === 'gift'
  ) {
    // Single-agreement shortcut → POST /case. `gift` shares the exact wire
    // mechanics of `immediate-transfer` (one immediate transfer); only the
    // business `type` differs (`gift` = безвозмездно vs `transfer` =
    // возмездный платёж). See SpecFullType enum, schemas/common.ts.
    return {
      spec_full: {
        type: template.id === 'gift' ? 'gift' : 'transfer',
        parties: [addrA, addrB],
        transfer: {
          id: `agr_demo_${template.id}_${runNonce}`,
          type: 'transfer',
          from: addrA,
          to: addrB,
          amount,
          execute_at: executeAtIso,
        },
        ...(wireAttachments !== undefined ? { attachments: wireAttachments } : {}),
      },
    };
  }
  if (template.id === 'delivery-with-repayment') {
    const principal = amount;
    const remainderPct = 0.5;
    const principalNum = Number.parseFloat(amount);
    const remainderRaw = Number.isFinite(principalNum)
      ? (principalNum * remainderPct).toFixed(0)
      : amount;
    return {
      spec_full: {
        type: 'loan',
        template: 'loan',
        parties: [addrA, addrB],
        agreements: [
          {
            id: `agr_demo_principal_${runNonce}`,
            type: 'transfer',
            from: addrA,
            to: addrB,
            amount: principal,
            execute_at: 'now',
          },
          {
            id: `agr_demo_delivery_${runNonce}`,
            type: 'transfer',
            from: addrB,
            to: addrA,
            amount: remainderRaw,
            execute_at: executeAtIso,
          },
        ],
        ...(wireAttachments !== undefined ? { attachments: wireAttachments } : {}),
      },
    };
  }
  if (template.id === 'delivery-with-prepayment-guarantee') {
    // Patent §8.4 «Поставка с предоплатой + гарантией возврата». Composite
    // contract type=delivery_with_prepayment (SpecFullType enum,
    // schemas/common.ts:117). Two agreements:
    //   A1: buyer→seller, execute_at='now'           — предоплата немедленно
    //   A2: seller→buyer, execute_at=<scheduled>     — обратное обязательство:
    //       если поставка не зафиксирована актом к дате X, TimeKeeper
    //       триггерит возврат на mock-симуляции (на боевом Fabric это
    //       chaincode scheduled-action). Сумма A2 = full prepayment
    //       (защита 100%, не частичная). Wire-сценарий-аналог:
    //       `contract-delivery-with-prepayment` (error-scenarios.ts:1971).
    return {
      spec_full: {
        type: 'delivery_with_prepayment',
        template: 'delivery_with_prepayment',
        parties: [addrA, addrB],
        agreements: [
          {
            id: `agr_demo_prepay_${runNonce}`,
            type: 'transfer',
            from: addrA,
            to: addrB,
            amount,
            execute_at: 'now',
          },
          {
            id: `agr_demo_refund_${runNonce}`,
            type: 'transfer',
            from: addrB,
            to: addrA,
            amount,
            execute_at: executeAtIso,
          },
        ],
        ...(wireAttachments !== undefined ? { attachments: wireAttachments } : {}),
      },
    };
  }
  if (template.id === 'delivery-post-payment') {
    // Patent §8.5 «Поставка с оплатой по факту» — composite contract
    // type=delivery_against_act per SpecFullType enum в schemas/common.ts:118.
    // Both agreements scheduled (no `now`): delivery (seller→buyer) +
    // payment (buyer→seller). Real closure happens через сериями actов —
    // wizard creates only the contract; act submissions live in Failure-
    // Mode panel / Scenario Runner. Counterpart wire scenario =
    // `contract-delivery-post-payment` (#21).
    return {
      spec_full: {
        type: 'delivery_against_act',
        template: 'delivery_against_act',
        parties: [addrA, addrB],
        agreements: [
          {
            id: `agr_demo_delivery_pp_${runNonce}`,
            type: 'transfer',
            from: addrB,
            to: addrA,
            amount,
            execute_at: executeAtIso,
          },
          {
            id: `agr_demo_payment_pp_${runNonce}`,
            type: 'transfer',
            from: addrA,
            to: addrB,
            amount,
            execute_at: executeAtIso,
          },
        ],
        ...(wireAttachments !== undefined ? { attachments: wireAttachments } : {}),
      },
    };
  }
  return {};
}
